import React from "react";
import EventFlyer from "./EventFlyer";
import EventShareActions from "./EventShareActions";
import EventPageMetadata from "./EventPageMetadata";
import { normalizeExternalUrl } from "../urlUtils";
import {
  formatEventDateLabel,
  formatEventTimeLabel,
  getEventCostLabel,
  normalizeEventRecord,
} from "../eventViewModel";

function eventMapUrl(event) {
  const latitude = event.latitude ?? event.lat;
  const longitude = event.longitude ?? event.lng;
  const query = latitude !== undefined && longitude !== undefined
    ? `${latitude},${longitude}`
    : [event.venueName, event.address, event.postcode].filter(Boolean).join(", ");

  return query
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
    : "";
}

export default function EventDetail({ event }) {
  const item = normalizeEventRecord(event);
  const timeLabel = formatEventTimeLabel(item);
  const costLabel = getEventCostLabel(item);
  const bookingUrl = normalizeExternalUrl(item.bookingUrl);
  const websiteUrl = normalizeExternalUrl(item.website);
  const mapUrl = eventMapUrl(item);
  const addressLabel = [item.address, item.postcode].filter(Boolean).join(", ");

  return (
    <article data-content-type="event-detail" className="min-w-0">
      <EventPageMetadata event={item} />

      <div className="grid min-w-0 gap-8 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
        <EventFlyer event={item} className="lg:sticky lg:top-6" />

        <div className="min-w-0 rounded-3xl border border-white/10 bg-white/5 p-5 md:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#F4A261]">
            {item.category || "Community event"}
          </p>
          <h1 className="mt-2 break-words text-3xl font-black leading-tight md:text-5xl">
            {item.title}
          </h1>

          {item.description ? (
            <p className="mt-5 whitespace-pre-line break-words text-base leading-7 text-white/75">
              {item.description}
            </p>
          ) : null}

          <dl className="mt-7 grid gap-5 border-y border-white/10 py-6 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-semibold uppercase tracking-wide text-white/50">Date</dt>
              <dd className="mt-1">{formatEventDateLabel(item)}</dd>
            </div>
            {timeLabel ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-white/50">Time</dt>
                <dd className="mt-1">{timeLabel}</dd>
              </div>
            ) : null}
            {item.venueName ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-white/50">Venue</dt>
                <dd className="mt-1 break-words">{item.venueName}</dd>
              </div>
            ) : null}
            {addressLabel ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-white/50">Address</dt>
                <dd className="mt-1 break-words">{addressLabel}</dd>
              </div>
            ) : null}
            {costLabel ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-white/50">Cost</dt>
                <dd className="mt-1">{costLabel}</dd>
              </div>
            ) : null}
            {item.organiserName ? (
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-white/50">Organiser</dt>
                <dd className="mt-1 break-words">{item.organiserName}</dd>
              </div>
            ) : null}
            {item.contact ? (
              <div className="sm:col-span-2">
                <dt className="text-xs font-semibold uppercase tracking-wide text-white/50">Public contact</dt>
                <dd className="mt-1 break-words">{item.contact}</dd>
              </div>
            ) : null}
          </dl>

          {(bookingUrl || websiteUrl || mapUrl) ? (
            <div className="mt-6 flex flex-wrap gap-3">
              {bookingUrl ? (
                <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center justify-center rounded-xl bg-[#E63946] px-5 py-3 font-bold text-white hover:opacity-90">
                  Book or register
                </a>
              ) : null}
              {websiteUrl ? (
                <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-5 py-3 font-semibold text-white hover:bg-white/15">
                  Event website
                </a>
              ) : null}
              {mapUrl ? (
                <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-12 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-5 py-3 font-semibold text-white hover:bg-white/15">
                  View location
                </a>
              ) : null}
            </div>
          ) : null}

          <EventShareActions event={item} />
        </div>
      </div>
    </article>
  );
}
